/**
 * Unified Underwriter Domain Model
 *
 * Single authoritative domain model for all underwriter data — enrichment,
 * matching, profiles, claims, and portal features.
 *
 * Table: underwriters
 *
 * Replaces:
 * - UnderwriterRegistry (current branch, enrichment pipeline)
 * - UnderwriterProfile (COV-288, claim workflow)
 * - Raw cova_match_underwriters schema (legacy)
 *
 * Field Access Control:
 * - id: DB-generated UUID
 * - dataQualityScore, lastEnrichmentAt, enrichmentStatus: Server-managed enrichment fields
 * - claimStatus, claimedByUserId, claimedAt, claimedByOrgId: Server-managed claim fields
 * - createdBy: Server-injected audit field
 * - createdAt, updatedAt: Server-managed timestamps
 */
import { DateTime, Schema } from "effect"
import { Model } from "@effect/sql"
import { FieldProvenance } from "./field-provenance"
import {
  ExternalContactPref,
  DEFAULT_EXTERNAL_CONTACT_PREF,
  canMessageExternally,
  canCallExternally,
} from "./external-contact-pref"
import { OwnerHiddenReasonCode } from "./owner-hidden-reason"

// =============================================================================
// Branded ID Types
// =============================================================================

/**
 * Underwriter ID
 *
 * Branded string type for UUID primary key.
 * Single identity across all consumers (enrichment, matching, profiles, claims).
 */
export const UnderwriterId = Schema.String.pipe(
  Schema.brand("UnderwriterId")
)
export type UnderwriterId = Schema.Schema.Type<typeof UnderwriterId>

// =============================================================================
// Enum / Literal Types
// =============================================================================

/**
 * Enrichment status of an underwriter record
 */
export const EnrichmentStatus = Schema.Literal(
  "pending",
  "in_progress",
  "completed",
  "failed"
)
export type EnrichmentStatus = Schema.Schema.Type<typeof EnrichmentStatus>

/**
 * Profile claim workflow status
 */
export const ClaimStatus = Schema.Literal(
  "unclaimed",
  "pending",
  "verified",
  "rejected"
)
export type ClaimStatus = Schema.Schema.Type<typeof ClaimStatus>

/**
 * Type of underwriting company
 */
export const CompanyType = Schema.Literal(
  "mga",
  "underwriting_agency",
  "lloyds_coverholder",
  "insurer",
  "reinsurer",
  "insurtech",
  "captive",
  "other"
)
export type CompanyType = Schema.Schema.Type<typeof CompanyType>

/**
 * Profile publication state.
 *
 * Persisted as a column on cova_underwriters (was previously derived from
 * isActive + claimStatus). Extended in spec 2026-05-21-001 with the three
 * submission lifecycle states (submitted, under_review, rejected).
 */
export const ProfileState = Schema.Literal(
  "draft",
  "submitted",
  "under_review",
  "rejected",
  "published",
  "claimed"
)
export type ProfileState = Schema.Schema.Type<typeof ProfileState>

/**
 * Cova Match subscription status
 */
export const CovaMatchStatus = Schema.Literal(
  "inactive",
  "active",
  "premium"
)
export type CovaMatchStatus = Schema.Schema.Type<typeof CovaMatchStatus>

/**
 * Capacity status for lines of business
 */
export const CapacityStatus = Schema.Literal("open", "limited", "closed", "referral_only")
export type CapacityStatus = Schema.Schema.Type<typeof CapacityStatus>

/**
 * Days a freshly claimed profile stays auto-featured in the marketplace
 * carousel (spec underwriters/2026-07-07-001). Internal mechanic, not
 * user-facing configuration.
 */
export const AUTO_FEATURED_DAYS = 14

/**
 * Australian state/territory codes
 */
export const AustralianState = Schema.Literal(
  "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"
)
export type AustralianState = Schema.Schema.Type<typeof AustralianState>

/**
 * Narrow a raw headquarters-state string to `[AustralianState]`, or `[]` when it
 * isn't a real Australian state. Some records were imported with a foreign HQ
 * (e.g. "England", "Cayman Islands") under region='AU'; casting those straight
 * into the closed AustralianState literal makes an RPC response fail to encode —
 * an unhandled defect (Die) that 500s the whole profile/directory load. Derives
 * from the literal itself so it can never drift from AustralianState.
 */
const AUSTRALIAN_STATE_SET: ReadonlySet<string> = new Set(AustralianState.literals)
export const toAustralianStates = (headquartersState: string | null | undefined): Array<AustralianState> =>
  headquartersState && AUSTRALIAN_STATE_SET.has(headquartersState)
    ? [headquartersState as AustralianState]
    : []

// =============================================================================
// Model Definition
// =============================================================================

/**
 * Unified Underwriter Domain Model
 *
 * Represents a single underwriting agency with enrichment data, profile info,
 * regulatory context, and claim workflow state.
 */
export class Underwriter extends Model.Class<Underwriter>("Underwriter")({
  // =========================================================================
  // Identity
  // =========================================================================

  /**
   * Primary key (UUID)
   * Auto-generated by database, excluded from insert schema
   */
  id: Model.Generated(UnderwriterId),

  /**
   * Name of the insurer / underwriting agency (legal name)
   */
  insurerName: Schema.String,

  /**
   * URL-safe identifier for public profile pages (e.g., "acme-insurance")
   * Nullable for seed data that hasn't been slugified yet
   */
  slug: Schema.NullOr(Schema.String),

  /**
   * Trading name (distinct from insurerName which is the legal name)
   */
  tradingAs: Schema.NullOr(Schema.String),

  // =========================================================================
  // Company Info
  // =========================================================================

  /**
   * Company website URL
   */
  website: Schema.NullOr(Schema.String),

  /**
   * Registrable domain (eTLD+1) of `website`, lowercased (e.g. "example.co.uk").
   * Server-managed: populated on write from `website` via
   * `extractRegistrableDomain` (domain-utils) so rows self-heal — never
   * user-supplied. Exact-match key for domain-gated discovery / claiming.
   *
   * Excluded from `jsonCreate`/`jsonUpdate`: this is the security boundary key a
   * domain match grants auto-admin on, so a client must never be able to set it.
   * Repositories still write it directly via raw SQL (`websiteDomainOf`).
   */
  websiteDomain: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Region code (e.g., "AU", "NZ", "UK", "US")
   * Determines which regulatory identifiers apply
   */
  region: Schema.String,

  /**
   * Array of policy types this underwriter supports
   * Stored as text[] in Postgres, uses GIN index for overlap queries
   */
  policyTypes: Schema.NullOr(Schema.Array(Schema.String)),

  /**
   * Free-text description of underwriter appetite
   */
  appetite: Schema.NullOr(Schema.String),

  /**
   * Free-text list of narrow niche specialties the underwriter handles
   * (e.g. "Timber hotels", "High-Voltage Electrical", "Treelopping").
   * Sibling to `appetite` — appetite captures the industries served;
   * specialties captures specific niches within those industries.
   */
  specialties: Schema.NullOr(Schema.String),

  /**
   * About / description of the underwriter
   */
  about: Schema.NullOr(Schema.String),

  /**
   * Physical address of headquarters
   */
  headquartersAddress: Schema.NullOr(Schema.String),

  /**
   * State/territory of headquarters
   */
  headquartersState: Schema.NullOr(Schema.String),

  /**
   * City of headquarters
   */
  headquartersCity: Schema.NullOr(Schema.String),

  /**
   * Year the company was founded
   */
  foundedYear: Schema.NullOr(Schema.Number),

  /**
   * Approximate number of employees
   */
  employeeCount: Schema.NullOr(Schema.Number),

  // =========================================================================
  // Company-level Contact (not individual contacts — those are in underwriter_contacts)
  // =========================================================================

  /**
   * Company-level phone number
   */
  phone: Schema.NullOr(Schema.String),

  /**
   * Company-level email
   */
  email: Schema.NullOr(Schema.String),

  /**
   * URL to company logo image
   */
  logoUrl: Schema.NullOr(Schema.String),

  // =========================================================================
  // Status & Quality (server-managed)
  // =========================================================================

  /**
   * Whether this underwriter is active
   */
  isActive: Schema.Boolean,

  /**
   * Curated flag surfaced by the broker-facing public directory featured section.
   * Super-admin managed.
   */
  isFeatured: Schema.Boolean,

  /**
   * Auto-featured window end (claim date + AUTO_FEATURED_DAYS), stamped at
   * claim finalisation by setClaimedOwnership. NULL = not auto-featured.
   * Passive expiry: the featured query compares against NOW() at read time.
   * Server-managed, excluded from client create/update.
   */
  featuredUntil: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Computed data quality score (0.00-1.00)
   * Stored as DECIMAL(3,2) in DB. Server-managed, excluded from client create/update
   */
  dataQualityScore: Schema.NullOr(Schema.NumberFromString).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Timestamp of last enrichment run
   * Server-managed, excluded from client create/update
   */
  lastEnrichmentAt: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Current enrichment pipeline status
   * Server-managed, excluded from client create/update
   */
  enrichmentStatus: Schema.NullOr(EnrichmentStatus).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  // =========================================================================
  // Provenance & Webset Linkage
  // =========================================================================

  /**
   * Per-field provenance tracking (JSONB)
   * Records where each field's data came from (source, references, timestamps)
   * pg driver returns JSONB as parsed objects — no JsonFromString needed
   * Server-managed, excluded from client create/update
   */
  fieldProvenance: FieldProvenance.pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Exa Company Webset item ID (match-once, link-forever)
   * Once an underwriter is matched to a Webset item, all future syncs are O(1)
   * Server-managed, excluded from client create/update
   */
  websetCompanyItemId: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  // =========================================================================
  // Claim Workflow (server-managed)
  // =========================================================================

  /**
   * Profile claim workflow status
   * Server-managed, excluded from client create/update
   */
  claimStatus: ClaimStatus.pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Clerk user ID who claimed the profile
   * Server-managed, excluded from client create/update
   */
  claimedByUserId: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Timestamp when the profile was claimed
   * Server-managed, excluded from client create/update
   */
  claimedAt: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Organisation UUID that owns this claimed profile
   * Server-managed, excluded from client create/update
   */
  claimedByOrgId: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Inbound external-broker contact policy for this profile: whether external
   * brokers may message and/or call the underwriter (spec chat/2026-07-22-001).
   * Set via the claimed-profile-write RPC (direct UPDATE), never via a Model
   * create/update payload. Optional-with-default so a row that predates the
   * v4.55 column (or a test fixture that omits it) decodes to the same value the
   * DB column defaults to — keeping decode backward-compatible before the
   * migration is applied everywhere.
   */
  externalContactPref: Schema.optionalWith(ExternalContactPref, {
    default: () => DEFAULT_EXTERNAL_CONTACT_PREF,
  }).pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),

  // =========================================================================
  // Owner-controlled marketplace visibility (spec 2026-09-09-001)
  // =========================================================================

  // Independent of isActive (the super-admin lever). optionalWith-default on
  // EVERY field so Underwriter.insert does not gain required keys (two insert
  // sites, one of them `as any`) and pre-migration rows still decode. Timestamps
  // are plain nullable DateTimeUtc, never Model.DateTimeInsert/Update — those
  // auto-stamp on every write and would mask the real hide instant (see the
  // note on `submittedAt`).
  ownerHidden: Schema.optionalWith(Schema.Boolean, { default: () => false }).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),
  ownerHiddenAt: Schema.optionalWith(Schema.NullOr(Schema.DateTimeUtc), { default: () => null }).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),
  ownerHiddenReasonCode: Schema.optionalWith(Schema.NullOr(OwnerHiddenReasonCode), {
    default: () => null,
  }).pipe(Model.FieldExcept("jsonCreate", "jsonUpdate")),
  ownerHiddenUntil: Schema.optionalWith(Schema.NullOr(Schema.DateTimeUtc), { default: () => null }).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  // The two SECRET fields are off EVERY json variant, not just the write
  // variants. `Model.Sensitive` = Field({ select, insert, update }) — byte-
  // identical to FieldExcept("json","jsonCreate","jsonUpdate") but named for the
  // intent (precedent: public-api/domain/credential/credential.model.ts:19).
  // FieldExcept("jsonCreate","jsonUpdate") alone would leave the field on
  // `Underwriter.json`, and NOT every broker-readable contract is an S.pick:
  // presentation/rpc/broker-annotations/schemas.ts builds AnnotatedUnderwriter
  // by extending the BARE json variant. The `select` variant is intact, so
  // repository decodes (SELECT *, `...Underwriter.fields` spreads) are unaffected.
  ownerHiddenByUserId: Model.Sensitive(
    Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null })
  ),
  /** Free text from the owner. Never logged, never tracked, never on ANY wire variant. */
  ownerHiddenReasonNote: Model.Sensitive(
    Schema.optionalWith(Schema.NullOr(Schema.String), { default: () => null })
  ),

  // =========================================================================
  // Submission Lifecycle (spec 2026-05-21-001)
  // =========================================================================

  /**
   * Persisted profile lifecycle state. Written exclusively by repo
   * transition methods (submitProfile, transitionToUnderReview, etc.) —
   * never by user input.
   */
  profileState: ProfileState.pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Clerk user id of the underwriter who self-submitted this profile.
   * NULL for admin-created rows. Promoted to `claimedByUserId` on approval.
   */
  submittedByUserId: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Organisation UUID of the submitter at submit time. Promoted to
   * `claimedByOrgId` on approval.
   */
  submittedByOrgId: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Timestamp the row was first submitted. Bumped only on resubmission
   * (rejected → submitted) — preserved across in-place edits while in
   * `submitted` state.
   *
   * Lifecycle field: written at SPECIFIC state transitions, NOT on every
   * insert/update. `Model.DateTimeInsert/Update` would auto-stamp on
   * every write, masking the real submission time.
   */
  submittedAt: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Timestamp the admin clicked "Start review". Lifecycle field — see
   * note on `submittedAt`.
   */
  reviewStartedAt: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Clerk user id of the super-admin who started review.
   */
  reviewStartedBy: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Free-text reason shown verbatim to the submitter and recorded for
   * audit. Required to set the `rejected` state.
   */
  rejectionReason: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Timestamp the row was rejected. Lifecycle field — see note on
   * `submittedAt`.
   */
  rejectedAt: Schema.NullOr(Schema.DateTimeUtc).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Clerk user id of the super-admin who rejected the submission.
   */
  rejectedBy: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  // =========================================================================
  // Audit
  // =========================================================================

  /**
   * Clerk user ID who created this underwriter record
   * Server-managed, excluded from client create/update
   */
  createdBy: Schema.NullOr(Schema.String).pipe(
    Model.FieldExcept("jsonCreate", "jsonUpdate")
  ),

  /**
   * Record creation timestamp
   */
  createdAt: Model.DateTimeInsert,

  /**
   * Record update timestamp
   */
  updatedAt: Model.DateTimeUpdate
}) {
  // ===========================================================================
  // Static Array Schemas
  // ===========================================================================

  static array = Schema.Array(Underwriter)
  static arrayJson = Schema.Array(Underwriter.json)

  // ===========================================================================
  // Computed Getters (Domain Logic)
  // ===========================================================================

  /**
   * Whether the enrichment data is stale (last enrichment > 7 days ago)
   * Returns true if never enriched or last enrichment is older than 7 days
   */
  get isStale(): boolean {
    return isEnrichmentStale(this, 7)
  }

  /**
   * Human-readable quality label based on data quality score (0.00-1.00)
   * - high: score >= 0.80
   * - medium: score >= 0.50
   * - low: score < 0.50 or null
   */
  get qualityLabel(): "high" | "medium" | "low" {
    if (this.dataQualityScore === null) return "low"
    if (this.dataQualityScore >= 0.80) return "high"
    if (this.dataQualityScore >= 0.50) return "medium"
    return "low"
  }

  /**
   * Whether this underwriter's profile has been claimed
   */
  get isClaimed(): boolean {
    return this.claimStatus === "verified"
  }

  /**
   * Whether this underwriter's profile has a pending claim
   */
  get hasPendingClaim(): boolean {
    return this.claimStatus === "pending"
  }

  /**
   * Whether an external broker may START a new message thread with this profile
   * (drives the marketplace "Message" button + the compose gate). Unclaimed
   * profiles stay reachable for lead-capture; claimed ones honour the preference.
   */
  get canBeMessagedExternally(): boolean {
    return canMessageExternally({
      claimStatus: this.claimStatus,
      externalContactPref: this.externalContactPref,
    })
  }

  /**
   * Whether an external broker's call may ring this profile (drives the call
   * gate). Unclaimed = false; claimed = only when calls are enabled.
   */
  get canBeCalledExternally(): boolean {
    return canCallExternally({
      claimStatus: this.claimStatus,
      externalContactPref: this.externalContactPref,
    })
  }
}

// =============================================================================
// Domain Invariant Functions
// =============================================================================

/**
 * Check if an underwriter's enrichment data is stale
 * Returns true if never enriched or older than the given threshold in days
 */
export const isEnrichmentStale = (
  underwriter: { lastEnrichmentAt: DateTime.Utc | null },
  thresholdDays: number = 7
): boolean => {
  if (underwriter.lastEnrichmentAt === null) return true
  const now = DateTime.unsafeNow()
  const thresholdMs = thresholdDays * 24 * 60 * 60 * 1000
  const diff = DateTime.toEpochMillis(now) - DateTime.toEpochMillis(underwriter.lastEnrichmentAt)
  return diff > thresholdMs
}

/**
 * Derive a quality label from a numeric score (0.00-1.00 range)
 */
export const qualityLabelFromScore = (
  score: number | null
): "high" | "medium" | "low" => {
  if (score === null) return "low"
  if (score >= 0.80) return "high"
  if (score >= 0.50) return "medium"
  return "low"
}

/**
 * Generate a URL-safe slug from an insurer name
 * - Lowercase, replace non-alphanumeric with hyphens
 * - Trim leading/trailing hyphens, collapse multiple hyphens
 */
export const generateSlug = (insurerName: string): string =>
  insurerName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")

/**
 * Check if a claim status transition is valid
 */
export const isValidClaimTransition = (
  from: ClaimStatus,
  to: ClaimStatus
): boolean => {
  const validTransitions: Record<ClaimStatus, readonly ClaimStatus[]> = {
    unclaimed: ["pending"],
    pending: ["verified", "rejected"],
    verified: ["unclaimed"], // Admin can unclaim
    rejected: ["pending", "unclaimed"]
  }
  return validTransitions[from]?.includes(to) ?? false
}

// =============================================================================
// Submission Lifecycle Helpers (spec 2026-05-21-001)
// =============================================================================

/**
 * A profile in `under_review` is locked for edits from any path except
 * super-admin (super-admin uses its own console / RPC group).
 */
export const isLockedForReview = (uw: {
  readonly profileState: ProfileState
}): boolean => uw.profileState === "under_review"

/**
 * True when the caller's organisation matches the claimed-by-org id.
 */
export const isOwnedByOrg = (
  uw: { readonly claimedByOrgId: string | null },
  orgId: string
): boolean => uw.claimedByOrgId === orgId

/**
 * True when the caller is the submitter (by user id or org id). Used
 * for pre-approval ownership checks where `claimedBy*` is still null.
 */
export const isOwnedBySubmitter = (
  uw: {
    readonly submittedByUserId: string | null
    readonly submittedByOrgId: string | null
  },
  userId: string,
  orgId: string
): boolean =>
  uw.submittedByUserId === userId || uw.submittedByOrgId === orgId

/**
 * Combined edit-permission predicate. Mirrors `requireProfileOwnership`
 * at the application/presentation layer so the frontend can pre-flight
 * the same gate before showing affordances.
 *
 * Rules:
 * - `under_review` → never (locked for any path except super-admin).
 * - `submitted` / `rejected` → submitter (by userId or orgId) only.
 * - `claimed` / `published` → claimed-by-org only.
 * - `draft` → neither (admin-only state).
 */
export const canEditAs = (
  uw: {
    readonly profileState: ProfileState
    readonly claimedByOrgId: string | null
    readonly submittedByUserId: string | null
    readonly submittedByOrgId: string | null
  },
  caller: { readonly userId: string; readonly orgId: string }
): boolean => {
  if (isLockedForReview(uw)) return false
  if (uw.profileState === "submitted" || uw.profileState === "rejected") {
    return isOwnedBySubmitter(uw, caller.userId, caller.orgId)
  }
  if (uw.profileState === "claimed" || uw.profileState === "published") {
    return isOwnedByOrg(uw, caller.orgId)
  }
  return false
}

/**
 * True when `orgId` owns the profile EITHER as its claimant OR as its submitter
 * (spec 2026-09-09-001 D-A).
 *
 * `isOwnedByOrg` above is claimed-only, which is the right rule where a claim is
 * a precondition. This is the broader org-level question two callers actually
 * ask — "may this org still see its own profile", independent of profile state —
 * and it exists so the owner-bypass read in `getProfileBySlug` and the write
 * guard in `require-profile-ownership.ts` cannot drift apart. Note it takes an
 * org only: unlike `isOwnedBySubmitter` there is no userId limb, because an org's
 * other members must see the profile their colleague submitted.
 *
 * PRECEDENCE: a claim, once it exists, is the ONLY owner. The submitter limb
 * applies while `claimedByOrgId` is still null (pre-approval, when the
 * submission is the only thing that ties an org to the profile). It is NOT an
 * OR on top of the claim: `ensureOrgForClaim` writes the claimant's org without
 * clearing `submittedByOrgId`, so an unconditional submitter limb would keep
 * the FORMER submitting org inside the write guard and the hidden-read bypass
 * after a different org had claimed the profile.
 */
export const isOwnedByOrgOrSubmitter = (
  uw: {
    readonly claimedByOrgId: string | null
    readonly submittedByOrgId: string | null
  },
  orgId: string
): boolean =>
  uw.claimedByOrgId !== null
    ? uw.claimedByOrgId === orgId
    : uw.submittedByOrgId !== null && uw.submittedByOrgId === orgId

// =============================================================================
// Public visibility (spec 2026-09-09-001 §5.5)
// =============================================================================

/**
 * The states a profile can be publicly listed in. Moved here from the
 * public-profiles impl (its local `PUBLIC_STATES`) so the rule has one home.
 */
export const PUBLIC_PROFILE_STATES: ReadonlyArray<ProfileState> = ["published", "claimed"]

/**
 * The structural subset every visibility predicate needs — so the service, the
 * handlers and the tests can call them without a fully decoded model.
 */
export type VisibilityFields = {
  readonly isActive: boolean
  readonly profileState: ProfileState
  readonly ownerHidden: boolean
  readonly ownerHiddenUntil: DateTime.Utc | null
}

/**
 * The owner lever, with LAZY EXPIRY: hidden only while `ownerHidden` is set AND
 * any auto-restore instant has not passed. `until == now` counts as restored.
 * No job ever flips the column back (the `featuredUntil` precedent).
 */
export const isEffectivelyOwnerHidden = (u: VisibilityFields, now: DateTime.Utc): boolean =>
  u.ownerHidden && (u.ownerHiddenUntil === null || DateTime.greaterThan(u.ownerHiddenUntil, now))

/**
 * THE public-visibility rule. Every broker/anonymous read path routes through
 * this, or through `publiclyVisibleFlagsClause`
 * (infrastructure/repository/underwriter/visibility-sql.ts) PLUS a profile_state
 * gate — the SQL fragment carries the flags only.
 */
export const isPubliclyVisible = (u: VisibilityFields, now: DateTime.Utc): boolean =>
  u.isActive &&
  PUBLIC_PROFILE_STATES.includes(u.profileState) &&
  !isEffectivelyOwnerHidden(u, now)

// =============================================================================
// Type Exports
// =============================================================================

export type UnderwriterInsert = typeof Underwriter.insert.Type
export type UnderwriterUpdate = typeof Underwriter.update.Type
export type UnderwriterJson = typeof Underwriter.json.Type
